import { asRecord, hasProperty } from "@/utils/record";

import { BackendError } from "../BackendError";
import { NoSuccessError } from "../NoSuccessError";
import type { CallContext } from "./requestHelper";

type ServerResponse = SuccessServerResponse | ErrorServerResponse;

interface SuccessServerResponse {
  success: true;
  response: unknown;
}

interface ErrorServerResponse {
  success: false;
  error: { message: string; code?: string; details?: unknown };
}

function hasResponseFormat(response: unknown): response is ServerResponse {
  return hasProperty(response, "success");
}

function isSuccessResponse(response: unknown): response is SuccessServerResponse {
  return hasResponseFormat(response) && response.success && "response" in response;
}

function isErrorResponse(response: unknown): response is ErrorServerResponse {
  return (
    hasResponseFormat(response) &&
    !response.success &&
    "error" in response &&
    "message" in response.error
  );
}

export function handleResponse(response: unknown, context: CallContext) {
  if (isSuccessResponse(response)) {
    return response.response;
  }
  if (isErrorResponse(response)) {
    throw new NoSuccessError(response.error.message, context.path, {
      request: context,
      response: response.error,
    });
  }
  throw new BackendError(
    "Bad Response Format",
    `Unexpected server response format calling RPC method ${context.path}`,
    { request: context, response: asRecord(response) ?? { value: response } },
  );
}
